import { Box, Button, Typography } from "@mui/material";
import { type FC } from "react";
import { NavLink } from "react-router";

import { ROUTES } from "~/constants/routing";
import { ArticleCardGrid } from "~/feature/insights/components/ArticleCardGrid";
import { InsightsPageShell } from "~/feature/insights/components/InsightsPageShell";
import { getInsightCategoryColor } from "~/feature/insights/constants";
import {
  type IInsight,
  type IInsightCategory,
  type IInsightsSidebarData,
} from "~/feature/insights/types";

import * as Styled from "../InsightsPage/InsightsPage.styles";

interface InsightsCategoryPageProps {
  insights: IInsight[];
  categories: IInsightCategory[];
  categoryName?: string;
  categoryDescription?: string;
  categoryColor?: string | null;
  showCategoryEmptyState?: boolean;
  sidebar?: IInsightsSidebarData;
}

export const InsightsCategoryPage: FC<InsightsCategoryPageProps> = ({
  insights,
  categories,
  categoryName,
  categoryDescription,
  categoryColor,
  showCategoryEmptyState = false,
  sidebar,
}) => {
  const color = getInsightCategoryColor(categoryColor);

  return (
    <InsightsPageShell
      categories={categories}
      sidebar={sidebar}
      header={
        <>
          <Styled.PageTitle variant="h3" component="h1" align="center">
            {categoryName ? (
              <Box component="span" sx={{ color }}>
                {categoryName}
              </Box>
            ) : (
              "Insights"
            )}
          </Styled.PageTitle>
          <Styled.PageSubtitle
            variant="body1"
            color="text.secondary"
            align="center"
          >
            {categoryDescription ||
              "Analysis, predictions, and breakdowns from across the markets."}
          </Styled.PageSubtitle>
        </>
      }
    >
      {insights.length === 0 ? (
        <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
          {showCategoryEmptyState ? (
            <>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mb: 3 }}
              >
                No articles in this category yet. Browse all insights.
              </Typography>
              <Button
                component={NavLink}
                to={ROUTES.insights.createURL()}
                variant="contained"
                sx={{ fontWeight: 600 }}
              >
                Browse all insights
              </Button>
            </>
          ) : (
            "No articles found."
          )}
        </Box>
      ) : (
        <ArticleCardGrid insights={insights} />
      )}
    </InsightsPageShell>
  );
};
